import { ReactRenderer } from "@tiptap/react";
import posthog from "posthog-js";
import tippy from "tippy.js";

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { FiLoader } from "react-icons/fi";
import { HiArrowTopRightOnSquare } from "react-icons/hi2";

import { resolvePopupContainer } from "@/components/email-composer/editor/popup-container";
import { ProgressBarLink } from "@/components/progress-bar";

import { cx } from "@/lib/tailwind-utils";

import {
  SuggestionItem,
  SuggestionType,
  suggestionPickerText,
} from "@/types/suggestions";

// Define the order of types for consistent display
export const TYPE_ORDER: SuggestionType[] = [
  "TEAM",
  // Snippets people wrote themselves come before the event links and variables.
  "PERSONAL",
  "TENANT",
  "SAVED",
  "EVENT",
  "NOTE_TEMPLATE",
  "ASSISTANT",
  "PATIENT",
  // After PATIENT, which is where an author looks for it in the form builder's picker.
  // Safe to move: chat and tasks render the TEAM group on its own, so nothing else here
  // has a position that shows.
  "PROVIDER",
  "MEMBERSHIP",
  // Directly after MEMBERSHIP, so the plan and the enrolment in it read as a pair.
  "MEMBERSHIP_ENROLLMENT",
  "EMAIL",
];

export const TYPE_LABELS: Record<SuggestionType, string> = {
  PERSONAL: "My Suggestions",
  SAVED: "Saved Responses",
  TENANT: "Organization Templates",
  NOTE_TEMPLATE: "Note Templates",
  ASSISTANT: "Assistant Suggestions",
  PATIENT: "Patient Information",
  MEMBERSHIP: "Membership Plan",
  MEMBERSHIP_ENROLLMENT: "Membership Enrollment",
  EVENT: "Event Information",
  EMAIL: "Email Variables",
  PROVIDER: "Provider",
  TEAM: "Team Members",
};

/**
 * Buckets suggestions by {@link SuggestionType} and returns them in
 * {@link TYPE_ORDER}, dropping empty groups. Single source of truth for the
 * grouped display shared by the @-mention popup and the "Insert variable" modal.
 */
export function groupSuggestions(
  items: SuggestionItem[],
): (readonly [SuggestionType, SuggestionItem[]])[] {
  const groups: Record<SuggestionType, SuggestionItem[]> = {
    PERSONAL: [],
    SAVED: [],
    TENANT: [],
    NOTE_TEMPLATE: [],
    ASSISTANT: [],
    PATIENT: [],
    MEMBERSHIP: [],
    MEMBERSHIP_ENROLLMENT: [],
    EVENT: [],
    EMAIL: [],
    PROVIDER: [],
    TEAM: [],
  };

  items.forEach((item) => {
    groups[item.type].push(item);
  });

  // Return groups in the defined order, filtering out empty groups
  return TYPE_ORDER.map((type) => [type, groups[type]] as const).filter(
    ([_, items]) => items.length > 0,
  );
}

interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const MentionList = forwardRef<
  MentionListRef,
  {
    items: SuggestionItem[];
    command: (item: SuggestionItem) => void;
    isLoading?: boolean;
    insertsText?: boolean;
  }
>(({ items, command, isLoading = false, insertsText = false }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const groupedItems = useMemo(() => groupSuggestions(items), [items]);

  // Create a flat array of items in the correct order for keyboard navigation
  const orderedItems = useMemo(() => {
    return groupedItems.flatMap(([_, items]) => items);
  }, [groupedItems]);

  const itemIndexMap = useMemo(
    () => new Map(orderedItems.map((item, idx) => [item.id, idx])),
    [orderedItems],
  );

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (orderedItems.length === 0) {
        return ["ArrowUp", "ArrowDown", "Enter"].includes(event.key);
      }

      if (event.key === "ArrowUp") {
        setSelectedIndex(
          (prev) => (prev + orderedItems.length - 1) % orderedItems.length,
        );
        return true;
      }

      if (event.key === "ArrowDown") {
        setSelectedIndex((prev) => (prev + 1) % orderedItems.length);
        return true;
      }

      if (event.key === "Enter") {
        selectItem(selectedIndex);
        return true;
      }

      return false;
    },
  }));

  const selectItem = (index: number) => {
    const item = orderedItems[index];
    if (item) {
      command(item);
    }
  };

  return (
    <div className="flex max-h-[min(400px,50vh)] flex-col overflow-auto pointer-events-auto rounded-lg border shadow-lg backdrop-blur-sm">
      {isLoading ? (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 bg-background/90 px-3 py-2 text-body text-muted-foreground"
        >
          <FiLoader className="h-4 w-4 animate-spin" />
          Searching...
        </div>
      ) : orderedItems.length > 0 ? (
        groupedItems.map(([type, typeItems], groupIndex) => (
          <div key={type} className="flex flex-col">
            <div className="sticky top-0 z-10 flex items-center justify-between bg-background-secondary px-3 py-1">
              <span className="text-caption-strong text-muted-foreground">
                {TYPE_LABELS[type as SuggestionType]}
              </span>
              {(type === "SAVED" || type === "PERSONAL") && (
                <ProgressBarLink
                  href={
                    type === "PERSONAL"
                      ? "/settings/preferences"
                      : "/settings/suggestions"
                  }
                  newWindow
                  className="inline-flex items-center gap-0.5 text-caption-strong text-primary hover:text-primary/80"
                >
                  Edit/Add
                  <HiArrowTopRightOnSquare className="h-3 w-3" />
                </ProgressBarLink>
              )}
            </div>
            {typeItems.map((item, index) => {
              const itemIndex = itemIndexMap.get(item.id) ?? -1;
              return (
                <button
                  ref={itemIndex === selectedIndex ? selectedRef : undefined}
                  key={item.id}
                  onClick={() => command(item)}
                  className={cx(
                    "flex w-full flex-col items-start border-b px-3 py-2 text-left transition-colors duration-100",
                    itemIndex === selectedIndex
                      ? "bg-primary/90 *:!text-white hover:bg-primary hover:*:!text-white"
                      : "bg-background/90 hover:bg-background-secondary",
                    index === typeItems.length - 1 &&
                      groupIndex !== groupedItems.length - 1 &&
                      "border-b-0",
                  )}
                >
                  <p className="text-s font-medium text-foreground">
                    {item.label}
                  </p>
                  <p className="leading-0 line-clamp-2 whitespace-pre-wrap break-words text-caption text-muted-foreground">
                    {suggestionPickerText(item, { insertsText })}
                  </p>
                </button>
              );
            })}
          </div>
        ))
      ) : (
        <div className="px-3 py-2 text-body text-muted-foreground">
          No suggestions found
        </div>
      )}
    </div>
  );
});

MentionList.displayName = "MentionList";

export const suggestion = {
  /**
   * `insertsText` says whether picking an item inserts its content as text
   * (rows show the content) or as a pill (rows show the preview).
   */
  render: ({ insertsText }: { insertsText?: () => boolean } = {}) => {
    let component: ReactRenderer<MentionListRef>;
    let popup: ReturnType<typeof tippy>[0];

    const openPopup = (props: any, isLoading = false) => {
      component = new ReactRenderer(MentionList, {
        props: { ...props, isLoading, insertsText: insertsText?.() ?? false },
        editor: props.editor,
      });

      popup = tippy("body", {
        getReferenceClientRect: props.clientRect,
        appendTo: () => resolvePopupContainer(props.editor),
        content: component.element,
        showOnCreate: true,
        interactive: true,
        trigger: "manual",
        placement: "bottom-start",
        popperOptions: {
          modifiers: [
            {
              name: "flip",
              options: {
                fallbackPlacements: ["top-start"],
              },
            },
            {
              name: "preventOverflow",
              options: {
                altAxis: true,
                tether: false,
              },
            },
          ],
        },
      })[0];
    };

    return {
      onBeforeStart: (props: any) => {
        openPopup(props, true);
      },

      onBeforeUpdate: (props: any) => {
        component?.updateProps({ ...props, isLoading: true });
        popup?.setProps({
          getReferenceClientRect: props.clientRect,
        });
      },

      onStart: (props: any) => {
        posthog.capture("suggestion_popup_opened");
        if (component) {
          component.updateProps({ ...props, isLoading: false });
          popup?.setProps({
            getReferenceClientRect: props.clientRect,
          });
          return;
        }

        openPopup(props);
      },

      onUpdate(props: any) {
        component?.updateProps({ ...props, isLoading: false });

        popup?.setProps({
          getReferenceClientRect: props.clientRect,
        });
      },

      onKeyDown(props: any) {
        if (props.event.key === "Escape") {
          popup?.hide();
          return true;
        }

        return component?.ref?.onKeyDown(props) ?? false;
      },

      onExit() {
        popup?.destroy();
        component?.destroy();
      },
    };
  },
};
